import React, { useEffect, useState, useCallback } from "react";
import ListSidebar from "../components/lists/ListSidebar";
import TaskContentArea from "../components/tasks/TaskContentArea";
import SEOHead from "@/components/SEOHead";
import { CANONICAL_URLS } from "@/utils/canonicalUrls";
import PriorityMatrixPanel from "../components/tasks/PriorityMatrixPanel";
import TaskListModalFABs from "@/components/tasks/modal/TaskListModalFABs";
import { useLists } from "@/context/ListContext";
import { useTasks } from "@/context/TaskContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Kanban, LayoutGrid, X, List } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { TaskCreateModal } from "../components/tasks/TaskCreateModal";
import TaskLimitNudge from "@/components/nudges/TaskLimitNudge";
import ListCreationNudge from "@/components/nudges/ListCreationNudge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import TaskEditModal from "@/components/tasks/TaskEditModal";
import AuthModal from "@/components/auth/AuthModal";
import useLocalTaskLimits from "@/hooks/useLocalTaskLimits";
const TasksPage: React.FC = () => {
  const {
    activeListId,
    lists
  } = useLists();
  const {
    fetchTasks,
    loading,
    activeTask,
    addTask,
    taskToEdit,
    setTaskToEdit
  } = useTasks();
  const {
    user,
    session
  } = useAuth();
  const { canCreateTask, canCreateList } = useLocalTaskLimits();
  const [switching, setSwitching] = useState(false);
  const [lastListId, setLastListId] = useState<string | null>(null);
  const [showEisenhowerMatrix, setShowEisenhowerMatrix] = useState(false);
  const [listSheetOpen, setListSheetOpen] = useState(false);
  const [showTaskLimitNudge, setShowTaskLimitNudge] = useState(false);
  const [showListCreationNudge, setShowListCreationNudge] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // State for the 'create task' modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // For navigation
  const navigate = useNavigate();

  // New: handle FAB create & focus actions
  const handleCreateTask = useCallback(() => {
    if (!session && !canCreateTask()) {
      setShowTaskLimitNudge(true);
      return;
    }
    setShowCreateModal(true);
  }, [session, canCreateTask]);
  
  const handleStartFocus = useCallback(() => {
    // Trigger focus timer or modal logic
    const evt = new CustomEvent("start-focus-timer");
    window.dispatchEvent(evt);
  }, []);

  // Save new task
  const handleSaveTask = (title: string, detail: string) => {
    addTask(title, detail);
  };

  // Handle list switch transitions for smoother UX
  useEffect(() => {
    if (activeListId && activeListId !== lastListId) {
      setSwitching(true);
      fetchTasks(activeListId).finally(() => {
        setTimeout(() => {
          setSwitching(false);
          setLastListId(activeListId);
        }, 250);
      });
    }
  }, [activeListId]); // Only when activeListId changes

  // Find active list's name
  const activeList = lists.find(l => l.id === activeListId);
  const activeListName = activeList ? activeList.name : "";
  
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Task Management & Kanban Board",
    "description": "Organize your tasks with our powerful task management system featuring Kanban boards and Eisenhower Matrix",
    "url": CANONICAL_URLS.tasks,
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web"
  };

  return (
    <>
      <SEOHead
        title="Task Management - Organize Your Tasks & Projects"
        description="Advanced task management with Kanban boards and Eisenhower Matrix. Organize projects, prioritize tasks, and boost productivity with visual workflow management tools."
        keywords="task management, kanban board, eisenhower matrix, project organization, productivity app, task tracking"
        canonicalUrl={CANONICAL_URLS.tasks}
        schemaData={schemaData}
      />
      <div className="flex h-[100vh] bg-background">
      {/* Sidebar */}
      <div className={`
          hidden md:flex
          md:w-96 md:max-w-[400px] md:min-w-[340px]
          lg:w-[420px] lg:max-w-[440px] lg:min-w-[420px]
          bg-white border-r dark:bg-gray-900 dark:border-gray-800 flex-col
          transition-all duration-200
          shadow-lg shadow-gray-400/40 dark:shadow-black/40
          z-20
        `} style={{
      boxSizing: "border-box"
    }}>
        <ListSidebar 
          switching={switching} 
          activeListId={activeListId} 
          onShowListCreationNudge={() => setShowListCreationNudge(true)}
        />
      </div>
      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 bg-muted/20 relative">
        <div className="flex flex-col gap-0 pt-6 pb-2 px-4 md:px-6 relative">
          {/* Close button absolute top right */}
          <button type="button" className="hidden md:inline-flex absolute top-3 right-3 z-20 rounded-full p-2 text-muted-foreground hover:bg-gray-100 hover:text-red-500 focus:outline-none dark:hover:bg-gray-800/60 transition-colors" onClick={() => navigate("/")} aria-label="Close" tabIndex={0}>
            <X size={32} className="w-8 h-8" />
          </button>
          {/* Mobile header row: Lists + Close */}
          <div className="flex items-center justify-between md:hidden">
            <Sheet open={listSheetOpen} onOpenChange={setListSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="secondary" size="default" aria-label="Open lists">
                  <List className="mr-2 h-4 w-4" />
                  Lists
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[90vw] sm:w-96">
                <ListSidebar 
                  switching={switching} 
                  activeListId={activeListId} 
                  onItemSelected={() => setListSheetOpen(false)} 
                  onShowListCreationNudge={() => setShowListCreationNudge(true)}
                />
              </SheetContent>
            </Sheet>
            <button type="button" className="rounded-full p-2 text-muted-foreground hover:bg-gray-100 hover:text-red-500 focus:outline-none dark:hover:bg-gray-800/60 transition-colors" onClick={() => navigate("/")} aria-label="Close">
              <X size={28} className="w-7 h-7" />
            </button>
          </div>

          {/* Title below on mobile, inline on desktop */}
          {activeListName ? <h1 className="md:hidden mt-2 text-xl font-semibold text-foreground">
              {activeListName}
            </h1> : null}

          <div className="hidden md:flex items-center gap-3">
            <span className="font-bold text-2xl text-gray-900 dark:text-white font-lora">
              {activeListName || ""}
            </span>
          </div>
          {/* Kanban/Matrix toggle */}
          <div className="flex w-full items-center justify-start mt-3 mb-1">
            <Tabs value={showEisenhowerMatrix ? "matrix" : "kanban"} onValueChange={v => setShowEisenhowerMatrix(v === "matrix")} className="w-full md:w-auto">
              <TabsList className="bg-gray-100 dark:bg-gray-800 grid grid-cols-2 w-full md:inline-flex md:w-auto gap-2 shadow-none p-1 py-0 rounded-xl">
                <TabsTrigger value="kanban" aria-label="Kanban Board" className="w-full md:w-auto justify-center flex items-center gap-2 px-4 md:px-6 font-semibold text-base rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white py-[4px]">
                  <Kanban className="w-5 h-5" />
                  Kanban
                </TabsTrigger>
                <TabsTrigger value="matrix" aria-label="Eisenhower Matrix" className="w-full md:w-auto justify-center flex items-center gap-2 px-4 md:px-6 font-semibold text-base rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:text-gray-900 dark:data-[state=active]:text-white py-[5px]">
                  <LayoutGrid className="w-5 h-5" />
                  Matrix
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
          {showEisenhowerMatrix ? <PriorityMatrixPanel /> : <TaskContentArea switching={switching}
        // No prop needed: now TaskContentArea wires up selection and drag-n-drop
        />}
        </div>
        {/* Floating Action Buttons */}
        <TaskListModalFABs 
          isOpen={true} 
          isAuthenticated={!!user} 
          activeListId={activeListId} 
          activeTask={activeTask} 
          mainTab="tasks" 
          showPriorityMatrix={showEisenhowerMatrix} 
          onCreateTask={handleCreateTask} 
          onStartFocus={handleStartFocus}
          onShowTaskLimitNudge={() => setShowTaskLimitNudge(true)}
          canCreateTask={canCreateTask()}
        />
        <TaskCreateModal open={showCreateModal} onClose={() => setShowCreateModal(false)} onSave={handleSaveTask} />
        <TaskEditModal open={!!taskToEdit} onClose={() => setTaskToEdit(null)} />
        
        {/* Nudge Modals */}
        {showTaskLimitNudge && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <TaskLimitNudge 
              onCreateAccount={() => {
                setShowTaskLimitNudge(false);
                setShowAuthModal(true);
              }}
              onClose={() => setShowTaskLimitNudge(false)}
            />
          </div>
        )}
        
        {showListCreationNudge && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <ListCreationNudge 
              onCreateAccount={() => {
                setShowListCreationNudge(false);
                setShowAuthModal(true);
              }}
              onClose={() => setShowListCreationNudge(false)}
            />
          </div>
        )}
        
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialMode="signup"
        />
      </div>
    </div>
    </>
  );
};
export default TasksPage;